import React, { useState } from "react";
import { useTestSendNativeInvoice } from "@api/native-invoice.api";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { EmailRecipientsField } from "./EmailRecipientsField";
import { parseRecipientList } from "./nativeInvoiceFormHelpers";

interface Props {
    show: boolean;
    onClose: () => void;
    workspaceId: string;
    entityId: number;
    invoiceId: string;
    initialRecipient: string;
}

export const SendTestInvoiceEmailModal: React.FC<Props> = ({
    show,
    onClose,
    workspaceId,
    entityId,
    invoiceId,
    initialRecipient,
}) => {
    const { success, error } = useToaster();
    const sendTestEmail = useTestSendNativeInvoice();
    const [recipientValue, setRecipientValue] = useState(initialRecipient);
    const recipients = parseRecipientList(recipientValue);
    const hasRecipient = recipients.length > 0;
    const isSendDisabled = !hasRecipient || sendTestEmail.isPending;
    // Names the only field-level gate on this modal's Send button (a missing
    // or invalid recipient) so a disabled button always explains why, mirroring
    // getSendDisabledTooltip on the main Send action. Left undefined once a
    // recipient is present so the transient pending state (already shown by
    // the button's loading spinner) doesn't get a tooltip too.
    const sendDisabledTooltip = hasRecipient
        ? undefined
        : "Add a valid email to send a test email";

    const handleSend = async () => {
        if (isSendDisabled) {
            return;
        }
        try {
            await sendTestEmail.mutateAsync({
                workspaceId,
                entityId,
                invoiceId,
                to: recipients.join(", "),
            });
            success("Test email sent");
            onClose();
        } catch {
            error("Failed to send test email");
        }
    };

    return (
        <PlainModal show={show} onClose={onClose} size="md">
            <StandardModalHeader>Send test email</StandardModalHeader>
            <VStack gap={16} className="mb-4">
                <EmailRecipientsField
                    label="Send to"
                    value={recipientValue}
                    onChange={setRecipientValue}
                />
            </VStack>
            <HStack as="footer" gap={12} justify="end">
                <Button variant="secondary" size="md" onClick={onClose}>
                    Cancel
                </Button>
                <Tooltip content={sendDisabledTooltip}>
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        disabled={isSendDisabled}
                        loading={sendTestEmail.isPending}
                        onClick={() => {
                            void handleSend();
                        }}
                    >
                        Send test email
                    </ButtonWithLoader>
                </Tooltip>
            </HStack>
        </PlainModal>
    );
};
